<template>
  <div
    :class="{ 'h-full': fullHeight }"
    :data-cm-metadata="placementMetadata"
    :data-test-id="`cms-${section.name}`"
  >
    <component
      :is="component.hydration && !disableLazyHydrate ? LazyHydrate : 'div'"
      v-for="(component, i) in components"
      :key="component.id"
      :when="component.hydration"
      v-bind="component.hydrationOptions"
    >
      <NuxtErrorBoundary @error="logError($event as Error, component)">
        <div
          v-style="{
            display: getVisibilityStyles(component.hideOnBreakpoint),
            mb: hasMargin(component),
            top: component.isSticky ? {
              sm: pxToRem(header.sticky.margin),
              md: pxToRem(header.sticky.margin),
            } : {},
          }"
          :class="[
            {
              'container ': hasContainer(component),
              'sticky z-3': component.isSticky,
              'h-full': fullHeight,
            },
            classWrapper,
          ]"
          :data-placement="`cms-${section.name}-${i + 1}`"
          :data-test-id="`cms-${component.componentType}-${component.id}`"
          :data-tracking-id="component.delocalizedTracking"
          v-bind="component.dataTestVariant ? { 'data-test-variant': component.dataTestVariant } : {}"
        >
          <cms-section-position-provider :index="i">
            <component
              :is="`lazy-cms-${component.componentType}`"
              :class="{ 'h-full': fullHeight }"
              :content="component"
              :data-cm-metadata="componentMetadata(component.id)"
              :first-element="pageContext && firstSection && i === 0"
            />
          </cms-section-position-provider>
        </div>
      </NuxtErrorBoundary>
    </component>
  </div>
</template>

<script lang="ts" setup>
import type { Responsive } from '#types/common'
import type { Content } from '#types/content'
import type { HydrationMode } from '#types/hydration'
import type { Section } from '#types/page'
import { SectionContextKey } from '#content/contexts/section'
import mappings from '#content/mappings'
import LazyHydrate from '#core/components/base/lazy-hydrate/LazyHydrate.vue'

type ContentItem = {
  componentType: string
  hydration: HydrationMode
  hydrationOptions?: {
    observerOptions: IntersectionObserverInit
  }
  isSticky: boolean
  bottomMargins: Record<keyof Responsive, string>
} & Content

const props = defineProps<{
  /**
   * CMS Section with content components
   */
  section: Section | any
  /**
   * Use page context in case you need CMS controlled layout to be applied (container) and bottom spacing
   */
  pageContext?: boolean
  /**
   * Lazy load media objects like images and videos inside section
   * Use for elements that would not be visible on the first screen
   */
  lazyMedia?: boolean
  disableLazyHydrate?: boolean
  fullHeight?: boolean
  firstSection?: boolean
  classWrapper?: string
}>()

const sectionData = {
  fullHeight: props.fullHeight,
  lazy: props.section.lazy || props.lazyMedia,
  name: props.section.name,
  section: props.section
}

const injectedData = inject(SectionContextKey)

if (injectedData) {
  provide(SectionContextKey, {
    ...sectionData,
    parent: injectedData
  })
}
else {
  provide(SectionContextKey, sectionData)
}

const { isPreview } = useCms()
const { ds, components: { cms } } = useAppConfig()
const { $feature } = useNuxtApp()
const header = useHeaderStore()

const { componentSpacingMap, componentsWithoutContainer, componentsWithoutMargin } = cms.section
const { breakpoints } = ds

const components = computed<ContentItem[]>(() => (props.section.items || []).reduce((acc, item) => {
  if (!$feature.monetateConfig?.isMonetateActive && item.type === 'P13NExperience') return acc

  const config = resolveContentComponent(item, mappings)

  if (!config) return acc

  return [
    ...acc,
    {
      ...config,
      bottomMargins: Object.keys(breakpoints).reduce((acc, bp) => ({
        ...acc,
        [bp]: componentSpacingMap[item.bottomMargins?.[bp] || 'default']
      }), {}),
      ...(!props.pageContext && { isFullWidth: true })
    }
  ]
}, [] as ContentItem[]))

const logError = (e: Error, { componentType, id }: ContentItem) => {
  log.error(`${e?.message} | CMS Error: component - ${componentType}, id - ${id}`, { stackTrace: e.stack })
}

const placementMetadata = isPreview
  ? JSON.stringify([
      { _: `properties.placement-${props.section.name}` },
      { placementRequest: [{ isInLayout: true, hasItems: true, placementName: props.section.name }] }
    ])
  : undefined

const componentMetadata = (id: string) => isPreview ? `[{"_":{"$Ref":"content/${id}"}}]` : undefined

const hasContainer = ({ isFullWidth, name, type }: ContentItem): boolean =>
  props.pageContext
  && !componentsWithoutContainer?.includes(type)
  && !name?.includes('[fullwidth]')
  && !isFullWidth

const hasMargin = ({ type, bottomMargins }: ContentItem) =>
  !componentsWithoutMargin?.includes(type)
  && (props.pageContext || components.value.length > 1)
    ? bottomMargins
    : null
</script>
